import { useFormik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Alert, Flex, FormField, Modal, TextArea, TextInput } from '@percent/lemonade'
import { emailRegex } from '@percent/utility'
import { AddToAutoApprovalListModalProps } from './AddToAutoApprovalListModal.types'

const agentIsOnAllowListErrorCode = 'allow_list/agent_already_exists'
const agentIsOnRejectionListErrorCode = 'allow_list/agent_in_rejection_list'

export function AddToAutoApprovalListModal({ isOpen, setIsOpen, refresh }: Readonly<AddToAutoApprovalListModalProps>) {
  const { t } = useTranslation()
  const { autoApprovalListService } = useServices()
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const getErrorMessage = () => {
    switch (error?.code) {
      case agentIsOnAllowListErrorCode:
        return t('dialog.addToApprovalList.agentIsOnAllowList')
      case agentIsOnRejectionListErrorCode:
        return t('dialog.addToApprovalList.agentIsOnRejectionList')
      default:
        return t('dialog.somethingWentWrong')
    }
  }

  const [{ isLoading, error }, { apiFunc: addToAutoApprovalList }] = useMutation(
    autoApprovalListService.addToAutoApprovalList,
    () => {
      setShowConfirmationScreen(true)
      refresh()
    },
    () => setShowErrorMessage(true)
  )

  const { values, handleChange, errors, submitForm, isValid, dirty } = useFormik<{
    email: string
    organisationId: string
    notes?: string
  }>({
    initialValues: {
      email: '',
      organisationId: '',
      notes: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        email: Yup.string()
          .required('Required')
          .matches(emailRegex, t('errorMessage.validEmail'))
          .max(255, t('errorMessage.shouldNotExceed255')),
        organisationId: Yup.string().required('Required'),
        notes: Yup.string().max(255, t('errorMessage.shouldNotExceed255'))
      }),
    onSubmit: addToAutoApprovalList
  })

  const closeModal = () => {
    setIsOpen(false)
    setShowErrorMessage(false)
  }

  return (
    <Modal open={isOpen} onClose={closeModal}>
      {showConfirmationScreen && (
        <AcknowledgeModal
          handleClose={() => {
            closeModal()
            setShowConfirmationScreen(false)
          }}
          title={t('dialog.addToApprovalListConfirmation.title')}
          description={t('dialog.addToApprovalListConfirmation.description', { agentEmail: values.email })}
          result="positive"
          buttonText={t('button.done')}
        />
      )}

      {!showConfirmationScreen && (
        <ActionModal
          title={t('dialog.addToApprovalList.title')}
          primaryButtonText={t('button.addEmail')}
          primaryBtnTestId="add-to-approval-list"
          secondaryButtonText={t('button.cancel')}
          handleClose={closeModal}
          handleSubmit={submitForm}
          loading={isLoading}
          disabled={!isValid || !dirty}
        >
          {showErrorMessage && <Alert variant="error">{getErrorMessage()}</Alert>}

          <Flex direction="column" gap={8}>
            <FormField
              label={t('typography.agentsEmail')}
              statusMessage={errors.email}
              status={errors.email ? 'danger' : 'default'}
              data-testid="agent-email-input"
              necessity="required"
            >
              <TextInput name="email" onChange={handleChange} placeholder={t('typography.agentsEmailPlaceholder')} />
            </FormField>
          </Flex>

          <Flex direction="column" gap={8} mt={16}>
            <FormField
              label={t('typography.organisationId')}
              statusMessage={errors.organisationId}
              status={errors.organisationId ? 'danger' : 'default'}
              data-testid="agent-organisationId-input"
              necessity="required"
            >
              <TextInput
                name="organisationId"
                onChange={handleChange}
                placeholder={t('typography.organisationIdPlaceholder')}
              />
            </FormField>
          </Flex>

          <Flex direction="column" gap={8} mt={16}>
            <FormField
              label={t('typography.notes')}
              statusMessage={errors.notes}
              status={errors.notes ? 'danger' : 'default'}
              data-testid="agent-notes-input"
              necessity="optional"
            >
              <TextArea
                name="notes"
                maxLength={255}
                onChange={handleChange}
                placeholder={t('typography.notesPlaceholder')}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
